import { CalendarType, DayOfWeek } from '@lmt/shared-types';
import {
  AppointmentModel,
  ViewType,
} from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-react-scheduler';
import {
  addDays,
  endOfDay,
  getDay,
  nextDay,
  previousDay,
  setDay,
  startOfDay,
  startOfWeek,
  subDays,
} from 'date-fns';

import { useMemo } from 'react';

/*
 * Returns 1-based index of excluded days taking into account firstDayOfWeek.
 * E.g.:
 *  - if firstDayOfWeek is Tuesday (2) and excludedDays are Tuesday and Wednesday (2, 3), returns [1, 2]
 *  - if firstDayOfWeek is Tuesday (2) and excludedDays are Wednesday and Thursday (3, 4), returns [2, 3]
 */
export function convertDaysToIndex(
  excludedDays: DayOfWeek[],
  firstDayOfWeek: DayOfWeek,
) {
  return excludedDays.map((x) => {
    const result = x - firstDayOfWeek + 1;
    return result > 0 ? result : result + 7;
  });
}

export function findFirstNonExcludedDay(
  excludedDays: Array<DayOfWeek>,
  firstDayOfWeek: DayOfWeek,
): DayOfWeek {
  return [...Array(7).keys()]
    .map((x) => (firstDayOfWeek + x) % 7)
    .find((x) => !excludedDays.includes(x))!;
}

export function useExcludedDaysInfo(
  calendarType: ViewType,
  excludedDays: DayOfWeek[] | undefined,
  currentDateProps: Date,
  firstDayOfWeekIndexConfiguration: DayOfWeek,
): {
  firstDayOfWeekIndex: DayOfWeek;
  currentDate: Date;
  hasExcludedDays: boolean;
} {
  const hasExcludedDays = !!(
    (calendarType === 'week' || calendarType === 'timelineWeek') &&
    excludedDays &&
    excludedDays.length > 0
  );
  // firstDayOfWeek must not be in `excludedDays` (i.e. firstDayOfWeek must be visible), otherwise all events have width 1px
  const firstDayOfWeekIndex = useMemo(() => {
    if (
      !hasExcludedDays ||
      !excludedDays.includes(firstDayOfWeekIndexConfiguration)
    )
      return firstDayOfWeekIndexConfiguration;
    return findFirstNonExcludedDay(
      excludedDays,
      firstDayOfWeekIndexConfiguration,
    );
  }, [firstDayOfWeekIndexConfiguration, excludedDays, hasExcludedDays]);
  // if we changed the `firstDayOfWeekIndex` according to `excludedDays`, we need to make sure that we display the same dates
  // that we requested Appointments for (otherwise we'd see no appointments, if we are displaying the next week)
  const currentDate = useMemo(() => {
    if (firstDayOfWeekIndex === firstDayOfWeekIndexConfiguration)
      return currentDateProps;
    return setDay(
      startOfWeek(currentDateProps, {
        weekStartsOn: firstDayOfWeekIndexConfiguration,
      }),
      firstDayOfWeekIndex,
      {
        weekStartsOn: firstDayOfWeekIndexConfiguration,
      },
    );
  }, [currentDateProps, firstDayOfWeekIndex, firstDayOfWeekIndexConfiguration]);

  return {
    currentDate,
    firstDayOfWeekIndex,
    hasExcludedDays: hasExcludedDays,
  };
}

/*
 * Adjust start/end dates of Appointments, so that they are within non-excluded days
 */
export function correctAppointmentsAccordingToExcludedDays(
  appointments: AppointmentModel[],
  excludedDays: DayOfWeek[],
): AppointmentModel[] {
  const excludedDaysMap: Record<
    number,
    { presentDateBefore: DayOfWeek; presentDayAfter: DayOfWeek }
  > = {};
  const excludedDaysSet = new Set(excludedDays);
  excludedDays.forEach((x) => {
    excludedDaysMap[x] = {
      presentDateBefore: findNotExcludedDay(excludedDaysSet, x, 'past'),
      presentDayAfter: findNotExcludedDay(excludedDaysSet, x, 'future'),
    };
  });

  const filteredAppointments = appointments.map((x) => {
    let result = x;
    const startDate = x.startDate as Date;
    const endDate = x.endDate as Date;

    // if day of `startDate` is excluded, we replace it with 00:00 of next present day
    const excludedStartDayInfo = excludedDaysMap[getDay(startDate)];
    if (excludedStartDayInfo) {
      result = {
        ...x,
        startDate: startOfDay(
          nextDay(startDate, excludedStartDayInfo.presentDayAfter),
        ),
      };
    }

    // if day of `endDate`` is excluded, we replace it with 00:00 of previous present day
    const excludedEndDayInfo = excludedDaysMap[getDay(endDate)];
    if (excludedEndDayInfo) {
      result = {
        ...x,
        endDate: endOfDay(
          previousDay(endDate, excludedEndDayInfo.presentDateBefore),
        ),
      };
    }
    //console.log(result, excludedDaysMap);
    if (result.startDate >= result.endDate!) return undefined!;

    // Check if Appointment span multiple days (2+), and maybe some of these days are excluded.
    // If they are excluded, we subtract these days from current event
    let intermediateDay = startOfDay(addDays(startDate, 1));
    let daysToSubtract = 0;
    while (startOfDay(endDate) > intermediateDay) {
      const excludedEndDayInfo = excludedDaysMap[getDay(intermediateDay)];
      if (excludedEndDayInfo) {
        daysToSubtract++;
      }
      intermediateDay = addDays(intermediateDay, 1);
    }
    if (daysToSubtract) {
      result = {
        ...x,
        endDate: subDays(endDate, daysToSubtract),
      };
    }

    return result;
  });

  return filteredAppointments.filter((x) => !!x);
}

export function findNotExcludedDay(
  excludedDaysSet: Set<DayOfWeek>,
  x: DayOfWeek,
  direction: 'future' | 'past',
): DayOfWeek {
  let newDate = x;
  while (true) {
    newDate = (newDate + (direction === 'future' ? 1 : -1) + 7) % 7;
    if (!excludedDaysSet.has(newDate)) {
      return newDate;
    }
  }
}
